import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, StatusBadge } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminTickets() {
  useSEO("Admin · Tickets");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get("/admin/tickets").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openTicket = (t) => { api.get(`/admin/tickets/${t.id}`).then((r) => setSel(r.data)); };

  const send = async () => {
    if (!reply.trim()) return;
    await api.post(`/admin/tickets/${sel.id}/reply`, { message: reply });
    setReply("");
    const r = await api.get(`/admin/tickets/${sel.id}`); setSel(r.data); load();
  };
  const setStatus = async (status) => { await api.put(`/admin/tickets/${sel.id}/status`, { status }); toast.success("Status updated"); const r = await api.get(`/admin/tickets/${sel.id}`); setSel(r.data); load(); };

  return (
    <div>
      <AdminHeader title="Support Tickets" subtitle="Respond to member support requests" />
      <div className="card-surface rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-white/10"><th className="p-4">Subject</th><th className="p-4">Member</th><th className="p-4">Status</th><th className="p-4">Updated</th><th className="p-4"></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-slate-500">No tickets yet.</td></tr>
              : rows.map((t) => (
                <tr key={t.id} className="border-b border-white/5" data-testid={`admin-ticket-${t.id}`}>
                  <td className="p-4 text-white">{t.subject}</td>
                  <td className="p-4 text-slate-400">{t.user_email}</td>
                  <td className="p-4"><StatusBadge status={t.status} /></td>
                  <td className="p-4 text-slate-500 text-xs">{new Date(t.updated_at).toLocaleDateString()}</td>
                  <td className="p-4"><Button size="sm" variant="outline" className="h-8 border-white/15 text-white" onClick={() => openTicket(t)} data-testid={`open-ticket-${t.id}`}>Open</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-brand-surface border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="text-white flex items-center justify-between gap-4">{sel?.subject}
            <Select value={sel?.status} onValueChange={setStatus}>
              <SelectTrigger className="w-28 h-8 bg-white/5 border-white/10 text-xs" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
            </Select>
          </DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="admin-ticket-messages">
            {sel?.messages?.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.sender === "admin" ? "gradient-brand text-brand-navy" : "bg-white/5 text-slate-200"}`}>
                  <p className="text-[10px] uppercase font-mono opacity-70">{m.sender === "admin" ? "Admin" : sel.user_name}</p>
                  <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-end pt-2 border-t border-white/10">
            <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply..." data-testid="admin-reply-input" className="flex-1 bg-white/5 border-white/10 resize-none" />
            <Button onClick={send} className="gradient-brand text-brand-navy font-semibold" data-testid="admin-reply-send"><Send className="h-4 w-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
